import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Button, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel,
  Alert,
  CircularProgress,
  Chip
} from '@mui/material';
import { 
  PlayArrow as PlayIcon, 
  Stop as StopIcon,
  Videocam as CameraIcon
} from '@mui/icons-material';
import CourtHlsPlayer from './CourtHlsPlayer';

const CourtStreamingPanel = ({ 
  court, 
  onStartStream, 
  onStopStream, 
  isStreamActive,
  streamingLoading,
  hlsUrl: propHlsUrl = '',
  streamKey: propStreamKey = ''
}) => {
  const [selectedCamera, setSelectedCamera] = useState('');
  const [hlsUrl, setHlsUrl] = useState(propHlsUrl);
  const [streamKey, setStreamKey] = useState(propStreamKey);
  const [streamError, setStreamError] = useState(null);

  // Update local state when props change
  useEffect(() => {
    setHlsUrl(propHlsUrl);
    setStreamKey(propStreamKey);
  }, [propHlsUrl, propStreamKey, isStreamActive, court]);

  const handleStartStream = async () => {
    if (!selectedCamera) {
      setStreamError('Please select a camera');
      return;
    }

    const camera = court.cameras.find(cam => cam.id.toString() === selectedCamera);
    if (!camera) {
      setStreamError('Selected camera not found');
      return;
    }

    try {
      setStreamError(null);
      await onStartStream(court, camera.rtspUrl);
      // The HLS URL will be updated via props from the parent component
    } catch (error) {
      setStreamError(error.message || 'Failed to start stream');
    }
  };

  const handleStopStream = async () => {
    try {
      await onStopStream(court);
      // The HLS URL will be cleared via props from the parent component
    } catch (error) {
      setStreamError(error.message || 'Failed to stop stream');
    }
  };

  const availableCameras = court.cameras?.filter(camera => camera.active) || [];

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" className="mb-4 flex items-center">
          <CameraIcon className="mr-2" />
          Live Streaming
        </Typography>

        {/* Stream Status */}
        <Box className="mb-4">
          <Box className="flex items-center justify-between mb-2">
            <Typography variant="body2" className="text-gray-600">
              Stream Status
            </Typography>
            <Chip 
              label={isStreamActive ? 'Live' : 'Offline'} 
              color={isStreamActive ? 'success' : 'default'}
              size="small"
            />
          </Box>
          
          {streamKey && (
            <Typography variant="body2" className="text-gray-600 font-mono text-sm">
              Stream Key: {streamKey}
            </Typography>
          )}
        </Box>

        {/* Camera Selection */}
        {!isStreamActive && (
          <Box className="mb-4">
            <FormControl fullWidth size="small">
              <InputLabel>Select Camera</InputLabel>
              <Select
                value={selectedCamera}
                onChange={(e) => setSelectedCamera(e.target.value)}
                label="Select Camera"
              >
                {availableCameras.map((camera) => (
                  <MenuItem key={camera.id} value={camera.id.toString()}>
                    {camera.cameraName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Error Display */}
        {streamError && (
          <Alert severity="error" className="mb-4">
            {streamError}
          </Alert>
        )}

        {/* Video Player */}
        <Box className="mb-4">
          {hlsUrl ? (
            <Box className="relative bg-black rounded-lg overflow-hidden">
              <CourtHlsPlayer
                hlsUrl={hlsUrl}
                autoPlay={true}
                onError={(error) => {
                  setStreamError(error);
                }}
              />
            </Box>
          ) : isStreamActive ? (
            <Box className="relative bg-black rounded-lg overflow-hidden h-64 flex items-center justify-center">
              <Box className="text-center text-white">
                <CircularProgress color="inherit" />
                <Typography variant="body2" className="mt-2">
                  Waiting for stream URL...
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box className="relative bg-gray-100 rounded-lg overflow-hidden h-64 flex items-center justify-center">
              <Box className="text-center text-gray-600">
                <CameraIcon className="text-4xl mb-2" />
                <Typography variant="body2">
                  No active stream. Start a stream to view live video.
                </Typography>
              </Box>
            </Box>
          )}
        </Box>

        {/* Stream Controls */}
        <Box className="flex gap-2">
          {!isStreamActive ? (
            <Button
              variant="contained"
              startIcon={<PlayIcon />}
              onClick={handleStartStream}
              disabled={streamingLoading || !selectedCamera || availableCameras.length === 0}
              className="bg-green-600 hover:bg-green-700"
              fullWidth
            >
              {streamingLoading ? <CircularProgress size={20} /> : 'Start Stream'}
            </Button>
          ) : (
            <Button
              variant="contained"
              color="error"
              startIcon={<StopIcon />}
              onClick={handleStopStream}
              disabled={streamingLoading}
              fullWidth
            >
              {streamingLoading ? <CircularProgress size={20} /> : 'Stop Stream'}
            </Button>
          )}
        </Box>

        {/* Camera Info */}
        {availableCameras.length === 0 && (
          <Alert severity="warning" className="mt-4">
            No active cameras available for streaming.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

export default CourtStreamingPanel;
